import { AxiosError } from "axios";

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";
const INTERNAL_SERVER_ERROR_MESSAGE =
  "Internal server error. Please try again later.";

const MESSAGE_KEYS = [
  "message",
  "messages",
  "errors",
  "errorMessages",
  "details",
  "detail",
  "validationErrors",
  "violations",
  "constraints",
];

const IGNORED_NESTED_KEYS = new Set([
  "status",
  "statusCode",
  "code",
  "error",
  "timestamp",
  "path",
]);

const GENERIC_HTTP_ERROR_MESSAGES = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "method not allowed",
  "conflict",
  "unprocessable entity",
  "too many requests",
  "internal server error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
]);

export interface ApiErrorMessageOptions {
  fallbackMessage?: string;
  internalServerErrorMessage?: string;
  separator?: string;
}

type ApiErrorResponseData = {
  message?: unknown;
  messages?: unknown;
  errors?: unknown;
  errorMessages?: unknown;
  details?: unknown;
  detail?: unknown;
  validationErrors?: unknown;
  violations?: unknown;
  constraints?: unknown;
  error?: unknown;
  statusCode?: number;
  status?: number;
};

/**
 * Type guard to check if an error is an AxiosError with a response property.
 */
export function isAxiosErrorWithResponse(error: unknown): error is AxiosError {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    (error as AxiosError).isAxiosError === true &&
    "response" in error &&
    typeof (error as AxiosError).response !== "undefined"
  );
}

/**
 * Return true for low-information HTTP labels that should not be shown when
 * better validation messages are unavailable.
 */
function isGenericHttpErrorMessage(message: string) {
  const normalized = message.trim().toLowerCase();

  return (
    GENERIC_HTTP_ERROR_MESSAGES.has(normalized) ||
    /^request failed with status code \d+$/i.test(message)
  );
}

function normalizeMessage(message: string) {
  return message.replace(/\s+/g, " ").trim();
}

function pushUnique(messages: string[], message: string) {
  const normalized = normalizeMessage(message);

  if (
    normalized &&
    !isGenericHttpErrorMessage(normalized) &&
    !messages.includes(normalized)
  ) {
    messages.push(normalized);
  }
}

function pushMessageText(messages: string[], message: string) {
  message.split(/\r?\n/).forEach((line) => pushUnique(messages, line));
}

function getStatusCode(error: AxiosError) {
  const data = error.response?.data as ApiErrorResponseData | undefined;

  return error.response?.status || data?.statusCode || data?.status;
}

function extractMessagesFromValue(
  value: unknown,
  options: { allowObjectValues: boolean } = { allowObjectValues: false }
): string[] {
  const messages: string[] = [];

  if (typeof value === "string") {
    pushMessageText(messages, value);
    return messages;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    pushUnique(messages, String(value));
    return messages;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      extractMessagesFromValue(item, { allowObjectValues: true }).forEach(
        (message) => pushUnique(messages, message)
      );
    });
    return messages;
  }

  if (!value || typeof value !== "object") {
    return messages;
  }

  const record = value as Record<string, unknown>;
  let foundKnownMessageKey = false;

  MESSAGE_KEYS.forEach((key) => {
    if (key in record) {
      foundKnownMessageKey = true;
      extractMessagesFromValue(record[key], {
        allowObjectValues: true,
      }).forEach((message) => pushUnique(messages, message));
    }
  });

  if (options.allowObjectValues && !foundKnownMessageKey) {
    Object.entries(record).forEach(([key, item]) => {
      if (IGNORED_NESTED_KEYS.has(key)) {
        return;
      }

      extractMessagesFromValue(item, { allowObjectValues: true }).forEach(
        (message) => pushUnique(messages, message)
      );
    });
  }

  return messages;
}

/**
 * Extract API error messages from Axios and non-Axios errors.
 */
export function extractApiErrorMessages(
  error: unknown,
  options: ApiErrorMessageOptions = {}
): string[] {
  const fallbackMessage = options.fallbackMessage || DEFAULT_ERROR_MESSAGE;
  const internalServerErrorMessage =
    options.internalServerErrorMessage || INTERNAL_SERVER_ERROR_MESSAGE;

  if (isAxiosErrorWithResponse(error)) {
    const statusCode = getStatusCode(error);

    if (statusCode && statusCode >= 500) {
      return [internalServerErrorMessage];
    }

    const responseData = error.response?.data as
      | ApiErrorResponseData
      | undefined;
    const responseMessages = extractMessagesFromValue(responseData);

    if (responseMessages.length > 0) {
      return responseMessages;
    }

    if (
      typeof responseData?.error === "string" &&
      responseData.error.trim() &&
      !isGenericHttpErrorMessage(responseData.error)
    ) {
      return [normalizeMessage(responseData.error)];
    }

    if (error.message.trim() && !isGenericHttpErrorMessage(error.message)) {
      return [normalizeMessage(error.message)];
    }

    return [fallbackMessage];
  }

  if (typeof error === "string" && error.trim()) {
    const messages: string[] = [];
    pushMessageText(messages, error);
    return messages.length ? messages : [fallbackMessage];
  }

  if (error instanceof Error && error.message.trim()) {
    const messages: string[] = [];
    pushMessageText(messages, error.message);
    return messages.length ? messages : [fallbackMessage];
  }

  const messages = extractMessagesFromValue(error);

  if (messages.length > 0) {
    return messages;
  }

  return [fallbackMessage];
}

/**
 * Extract the most useful API error message from Axios and non-Axios errors.
 */
export function extractApiErrorMessage(
  error: unknown,
  fallbackMessage = DEFAULT_ERROR_MESSAGE,
  options: Omit<ApiErrorMessageOptions, "fallbackMessage"> = {}
): string {
  return extractApiErrorMessages(error, {
    ...options,
    fallbackMessage,
  }).join(options.separator ?? "\n");
}

/**
 * Mutate Axios errors once at the API boundary so existing `error.message`
 * call sites receive user-friendly backend validation details.
 */
export function normalizeApiError(
  error: unknown,
  options: ApiErrorMessageOptions = {}
) {
  if (!error || typeof error !== "object" || !("message" in error)) {
    return error;
  }

  const message = extractApiErrorMessage(
    error,
    options.fallbackMessage || DEFAULT_ERROR_MESSAGE,
    options
  );

  try {
    (error as Error).message = message;
  } catch {
    // Keep the original error object if its message is not writable.
  }

  return error;
}
